/**
 * determination.ts — rendering a safety DETERMINATION, and the rule that makes
 * it safe.
 *
 * THE RULE THIS FILE EXISTS TO ENFORCE
 * -----------------------------------
 * An undetermined condition must never be rendered as a determined one. The
 * failure this prevents is not a colour: it is a person reading a drawing,
 * seeing something that looks settled, and not going and check. "Unknown" and
 * "not assessed" are both ABSENCE OF A DETERMINATION, and a browser that
 * paints them the way it paints a settled state has manufactured a safety
 * claim the Engine never made.
 *
 * So the treatment ladder here is deliberately one-directional. There is no
 * `UNKNOWN => neutral-ish colour that reads as fine` branch, and no
 * "unrecognised value, assume benign" default. `resolveDetermination` can
 * return an AFFIRMATIVE treatment for a value only when the Engine published an
 * affirmative assertion FOR THAT VALUE. Absent that publication the answer is
 * `indeterminate`, and `indeterminate` is rendered as an explicit absence:
 * named, uncoloured, and different again from a reported value.
 *
 * WHY THE VALUES ARE NOT SPELLED OUT HERE
 * ---------------------------------------
 * There is no list of states in this file, and no field name. WP33 fixes the
 * Engine's determination vocabulary at four arms; duplicating that list in the
 * browser would make it a second source of truth that drifts the first time the
 * Engine adds a fifth. So the value is read from the descriptor's own schema,
 * and the browser's only editorial act is deciding how LACK of a value looks.
 *
 * The consequence worth stating: a determination the Engine has not published a
 * schema for cannot be rendered as determined by this browser, ever, no matter
 * what the payload says. Failing towards `indeterminate` is the correct default
 * for a safety vocabulary, and it is the default here.
 */

/** How a determination is presented. Ordered by increasing commitment. */
export type Treatment =
  /** The Engine published an affirmative assertion for this exact value. */
  | "affirmative"
  /** The Engine published a negative/adverse assertion for this exact value. */
  | "adverse"
  /** A value was reported, and it means the Engine declined to conclude. */
  | "indeterminate"
  /** No determination was reported at all. Distinct from `indeterminate`. */
  | "not-reported"
  /** The value is not one the Engine's schema enumerates. Never trusted. */
  | "unrecognised";

/** The treatments that may ever be rendered as a settled conclusion. */
export const SETTLED_TREATMENTS: ReadonlySet<Treatment> = new Set<Treatment>(["affirmative", "adverse"]);

export interface Determination {
  readonly treatment: Treatment;
  /** The Engine's own text, when the schema published a title for the value. */
  readonly label: string | undefined;
  /** Why this treatment, in words a reviewer can check. Never a guess. */
  readonly basis: string;
  /**
   * Whether a human must be shown something extra.
   *
   * True for every treatment that is not a settled, Engine-asserted one. A
   * reviewer looking at an `indeterminate` determination needs the basis text,
   * not just a colour.
   */
  readonly requiresAttention: boolean;
}

/** A JSON-Schema fragment carrying a bounded enumeration, if it has one. */
interface Enumeration {
  readonly values: readonly unknown[];
  /** Per-value presentation metadata, keyed by the value's own text. */
  readonly metadata: ReadonlyMap<string, Readonly<Record<string, unknown>>>;
  /** The Engine's own per-value wording, keyed by the value's own text. */
  readonly titles: ReadonlyMap<string, string>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** The keyword the Engine uses for per-value presentation metadata, if any. */
const VALUE_PRESENTATION_KEY = "x-presentation";

/**
 * Read a schema's enumeration, including per-value presentation metadata.
 *
 * A schema may publish its values as a bare `enum` array, as `oneOf` members
 * each carrying a `const`, or not at all. All three are read; none is assumed.
 */
export function readEnumeration(schema: unknown): Enumeration | undefined {
  if (!isRecord(schema)) return undefined;
  const values: unknown[] = [];
  const metadata = new Map<string, Record<string, unknown>>();
  const titles = new Map<string, string>();

  const record = (value: unknown, member: unknown): void => {
    values.push(value);
    const presentation = isRecord(member) ? member[VALUE_PRESENTATION_KEY] : undefined;
    if (isRecord(presentation) && typeof value === "string") metadata.set(value, presentation);
    // The Engine's own per-value TITLE, when it published one. Two arms that both
    // assert nothing still mean different things, and the Engine is the party
    // that said so — so its wording is relayed rather than replaced with a
    // generic phrase that would merge them back together.
    if (isRecord(member) && typeof member.title === "string" && typeof value === "string") {
      titles.set(value, member.title);
    }
  };

  if (Array.isArray(schema.enum)) {
    for (const value of schema.enum) record(value, value);
  }
  if (Array.isArray(schema.oneOf)) {
    for (const member of schema.oneOf) {
      if (!isRecord(member)) continue;
      if (member.const !== undefined) {
        record(member.const, member);
      } else if (Array.isArray(member.enum) && member.enum.length === 1) {
        record(member.enum[0], member);
      }
    }
  }
  return values.length > 0
    ? { values: Object.freeze([...values]), metadata, titles }
    : undefined;
}

/**
 * The presentation a determination may take, read from the Engine's own
 * per-value metadata.
 *
 * Only an explicit `affirmative` or `adverse` assertion produces a settled
 * treatment. Anything else — including a missing assertion, an assertion this
 * function does not recognise, and a value with no metadata at all — yields
 * `undefined`, and the caller falls back to `indeterminate`.
 *
 * The `indeterminate` case is the load-bearing one. A future Engine that adds
 * a fifth determination arm, or a value published without an assertion, gets
 * rendered as an absence of determination rather than being quietly treated as
 * the nearest old value. Not recognising a value must never mean concluding
 * something about it.
 */
export function publishedTreatment(presentation: Readonly<Record<string, unknown>> | undefined): Treatment | undefined {
  if (!presentation) return undefined;
  const assertion = presentation.assertion;
  if (assertion === "affirmative") return "affirmative";
  if (assertion === "adverse") return "adverse";
  return undefined;
}

/**
 * Resolve how to render one determination value.
 *
 * `schema` is the Engine's schema for the property, and `value` is what the
 * Engine's payload actually carried for it. They are separate arguments on
 * purpose: the common and dangerous case is a payload that omits the property
 * entirely, and that case must be distinguishable from a payload that carries a
 * value nobody recognises.
 */
export function resolveDetermination(schema: unknown, value: unknown): Determination {
  // The `schema` argument is trusted as a schema, and `undefined` is treated as
  // "the Engine published no schema for this property" — the conservative
  // reading. Guarding here rather than at the call sites means a caller cannot
  // accidentally pass a non-schema and get a settled treatment out of it.
  const enumeration = isRecord(schema) ? readEnumeration(schema) : undefined;

  // ABSENCE OF A VALUE. Distinct from every other case, because a viewer who
  // cannot tell "not reported" from "reported as not known" will eventually read
  // one as the other. There is no affirmative path that reaches this branch.
  if (value === undefined || value === null || value === "") {
    return {
      treatment: "not-reported",
      label: undefined,
      basis: "The Engine reported no determination for this property. Absence is shown as absence, never as a result.",
      requiresAttention: true,
    };
  }

  // A VALUE THE ENGINE'S SCHEMA DOES NOT ENUMERATE. Also never affirmative: an
  // unrecognised value is a contract the browser does not understand, and
  // guessing its meaning is precisely the failure this file is written against.
  if (enumeration && !enumeration.values.some(candidate => candidate === value)) {
    return {
      treatment: "unrecognised",
      label: typeof value === "string" ? value : undefined,
      basis: "This value is not in the Engine's published enumeration, so this browser draws no conclusion from it.",
      requiresAttention: true,
    };
  }

  const key = typeof value === "string" ? value : undefined;
  const presentation = key !== undefined ? enumeration?.metadata.get(key) : undefined;
  const title = key !== undefined ? enumeration?.titles.get(key) : undefined;
  const published = publishedTreatment(presentation);
  if (published !== undefined) {
    return {
      treatment: published,
      label: key,
      basis: `The Engine published this determination as ${published}.`,
      requiresAttention: false,
    };
  }

  // An unasserted value is reported and decides nothing. Where the Engine
  // published its own wording for the value, that wording is carried into the
  // basis: two arms can both assert nothing and still mean different things, and
  // only the Engine said which was which.
  return {
    treatment: "indeterminate",
    label: key,
    basis: enumeration
      ? `The Engine published this value without asserting a conclusion${title === undefined ? "" : `, in these terms: ${title}`}. It is reported, and it decides nothing.`
      : "The Engine published no schema for this property, so no conclusion is drawn from the reported value.",
    requiresAttention: true,
  };
}

/**
 * The one invariant, expressed so a test can assert it against every input.
 *
 * `not-reported` may only ever be produced by a payload that did not carry the
 * value. This exists as a function because a claim written only in a comment is
 * a claim nothing checks.
 */
export function absenceIsNeverDetermination(schema: unknown, value: unknown): boolean {
  if (value !== undefined && value !== null && value !== "") return true;
  return resolveDetermination(schema, value).treatment === "not-reported";
}
